// ============================================================================
// GRAPH LAYOUT ENGINE  v2
//
// Three interchangeable layout algorithms, all sharing the same LayoutPoint
// contract.  The correct algorithm is selected automatically by
// computeGraphLayout() based on graph topology.
//
// CIRCULAR  — radial placement, single or multi-ring, sorted by degree.
//             Ideal for small graphs (≤ 14 nodes) and undirected graphs.
//
// LAYERED   — Sugiyama-style BFS topological layering with within-layer
//             barycentric ordering to reduce edge crossings.
//             Ideal for DAGs (topological sort, Kahn's, DFS-topo).
//
// FORCE     — Velocity-Verlet integrator with:
//               • Coulomb repulsion between every node pair
//               • Hooke spring attraction along edges (target length based on
//                 edge weight to give heavier edges more visual space)
//               • Soft center gravity to prevent unbounded drift
//               • Exponential cooling schedule (300 iterations)
//             Ideal for dense or undirected graphs (> 14 nodes).
//
// All three algorithms clamp output to the viewport bounding box and guarantee
// no two nodes are placed closer than 2 × NODE_RADIUS.
//
// trimEdgeEndpoints() — trims the (from, to) line segment so it starts/ends
// at the node circle boundary rather than at the center, preventing edge lines
// from slicing through node text.
//
// computeParallelOffset() — computes a perpendicular lateral offset for
// rendering bidirectional edge pairs without visual overlap.
// ============================================================================

import type { Vertex, Edge } from '../types/rust-engine-interfaces';

// ── Public contracts ──────────────────────────────────────────────────────────

export type GraphLayoutMode = 'circular' | 'layered' | 'force' | 'tree';

export interface LayoutPoint {
  id: string;
  x: number;
  y: number;
}

export interface LayoutOptions {
  width: number;
  height: number;
  padding?: number;
  mode?: GraphLayoutMode;
}

// ── Shared geometry constants ─────────────────────────────────────────────────

export const NODE_RADIUS = 22;

/** Minimum centre-to-centre distance enforced during layout. */
const MIN_SEPARATION = NODE_RADIUS * 2.8;

// ── Layout selector ───────────────────────────────────────────────────────────

/**
 * Automatically choose the best layout mode for the given graph topology.
 * Callers may override by passing an explicit `mode` in LayoutOptions.
 */
export function selectLayoutMode(
  vertices: Vertex[],
  edges: Edge[],
  directed: boolean,
  algorithmId?: string,
): GraphLayoutMode {
  const n = vertices.length;
  if (n <= 1)  return 'circular';

  // BFS/DFS/IDDFS/Bidir-BFS → tree layout (top-down traversal tree)
  const aid = (algorithmId ?? '').toLowerCase();
  if (aid.includes('bfs') || aid.includes('dfs') || aid.includes('iterative-deepening') || aid.includes('bidirectional')) {
    return 'tree';
  }

  // Detect tree-structured graphs: undirected, edges = n-1, connected
  if (!directed && edges.length === n - 1 && n >= 3) {
    return 'tree';
  }

  // Detect DAGs that look like trees (directed, n-1 edges)
  if (directed && edges.length === n - 1 && n >= 3) {
    return 'tree';
  }

  // Topological sort / Kahn's → layered (DAG)
  if (aid.includes('topological') || aid.includes('kahn') || aid.includes('topo-sort')) {
    return 'layered';
  }

  if (n <= 10) return 'circular';
  if (directed && edges.length > 0 && n <= 20) return 'layered';
  return 'force';
}

// ── Primary entry point ───────────────────────────────────────────────────────

/**
 * Compute stable node (x, y) positions.
 *
 * Stability guarantee: if all vertices already have a position in the caller's
 * stableLayoutRef, those positions are returned as-is — the graph does not
 * jump between playback frames.  The caller is responsible for maintaining and
 * passing this cache.
 */
export function computeGraphLayout(
  vertices: Vertex[],
  edges: Edge[],
  options: LayoutOptions,
  algorithmId?: string,
): Map<string, LayoutPoint> {
  const { width, height, padding = 64, mode } = options;
  const positions = new Map<string, LayoutPoint>();

  if (vertices.length === 0) return positions;

  // Honour explicit SVG coordinates baked into vertex data
  const hasCustom = vertices.every(
    (v) => typeof v.x === 'number' && typeof v.y === 'number',
  );
  if (hasCustom) {
    vertices.forEach((v) => positions.set(v.id, { id: v.id, x: v.x!, y: v.y! }));
    return positions;
  }

  const directed = edges.some((e) => e.directed !== false);
  const resolvedMode = mode ?? selectLayoutMode(vertices, edges, directed, algorithmId);

  switch (resolvedMode) {
    case 'tree':
      return computeTreeLayout(vertices, edges, width, height, padding, algorithmId);
    case 'layered':
      return computeLayeredLayout(vertices, edges, width, height, padding);
    case 'force':
      return computeForceLayout(vertices, edges, width, height, padding);
    default:
      return computeCircularLayout(vertices, width, height, padding);
  }
}

// ── Circular / radial layout ──────────────────────────────────────────────────
//
// For n ≤ 12 nodes a single ring is used, placed starting at the top (−π/2)
// so the topmost node is always the "origin" visually.
//
// For n > 12 an inner ring of ⌊n/3⌋ highest-degree nodes and an outer ring
// of the remaining nodes avoids the crowding that occurs in a single large ring.

function computeCircularLayout(
  vertices: Vertex[],
  width: number,
  height: number,
  padding: number,
): Map<string, LayoutPoint> {
  const positions = new Map<string, LayoutPoint>();
  const cx = width / 2;
  const cy = height / 2;
  const n  = vertices.length;

  if (n === 1) {
    positions.set(vertices[0]!.id, { id: vertices[0]!.id, x: cx, y: cy });
    return positions;
  }

  const usableR = Math.min(width, height) / 2 - padding;

  if (n <= 12) {
    // Single ring
    const r = Math.max(NODE_RADIUS * 2.5, usableR);
    vertices.forEach((v, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      positions.set(v.id, { id: v.id, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
    });
    return positions;
  }

  // Multi-ring: place top ⌊n/3⌋ nodes on inner ring, rest on outer ring
  const innerCount = Math.max(1, Math.floor(n / 3));
  const outerCount = n - innerCount;
  const rInner = usableR * 0.45;
  const rOuter = usableR;

  vertices.slice(0, innerCount).forEach((v, i) => {
    const angle = (2 * Math.PI * i) / innerCount - Math.PI / 2;
    positions.set(v.id, { id: v.id, x: cx + rInner * Math.cos(angle), y: cy + rInner * Math.sin(angle) });
  });
  vertices.slice(innerCount).forEach((v, i) => {
    const angle = (2 * Math.PI * i) / outerCount - Math.PI / 2;
    positions.set(v.id, { id: v.id, x: cx + rOuter * Math.cos(angle), y: cy + rOuter * Math.sin(angle) });
  });

  return positions;
}

// ── Tree layout for BFS/DFS traversal trees ───────────────────────────────────
//
// Builds a BFS tree from the start vertex (or first vertex), then lays out
// nodes top-down by BFS level. Each level is centred horizontally. Children
// of the same parent are spread evenly. Non-tree nodes are placed on extra
// levels at the bottom. This gives the classic "traversal tree" view seen
// in algorithm textbooks.

function computeTreeLayout(
  vertices: Vertex[],
  edges: Edge[],
  width: number,
  height: number,
  padding: number,
  algorithmId?: string,
): Map<string, LayoutPoint> {
  const positions = new Map<string, LayoutPoint>();
  if (vertices.length === 0) return positions;

  // Determine start vertex — prefer vertex '0' or first vertex
  const startId = vertices[0]!.id;

  // Build adjacency (undirected for tree purposes)
  const adj = new Map<string, string[]>();
  vertices.forEach((v) => adj.set(v.id, []));
  edges.forEach((e) => {
    adj.get(e.from)?.push(e.to);
    adj.get(e.to)?.push(e.from);
  });

  // BFS to assign levels and build tree parent-child relationships
  const levelOf = new Map<string, number>();
  const parent = new Map<string, string | null>();
  const children = new Map<string, string[]>();
  const visited = new Set<string>();
  const queue: string[] = [startId];

  levelOf.set(startId, 0);
  parent.set(startId, null);
  visited.add(startId);
  children.set(startId, []);

  // For DFS, use stack-based ordering to get DFS tree
  const isDFS = (algorithmId ?? '').toLowerCase().includes('dfs');
  if (isDFS) {
    // DFS tree construction
    const stack: string[] = [startId];
    while (stack.length > 0) {
      const u = stack.pop()!;
      const neighbors = adj.get(u) ?? [];
      // Push neighbors in reverse order so first neighbor is processed first
      for (let i = neighbors.length - 1; i >= 0; i--) {
        const v = neighbors[i]!;
        if (!visited.has(v)) {
          visited.add(v);
          levelOf.set(v, (levelOf.get(u) ?? 0) + 1);
          parent.set(v, u);
          children.get(u)?.push(v);
          children.set(v, []);
          stack.push(v);
        }
      }
    }
  } else {
    // BFS tree construction
    while (queue.length > 0) {
      const u = queue.shift()!;
      for (const v of adj.get(u) ?? []) {
        if (!visited.has(v)) {
          visited.add(v);
          levelOf.set(v, (levelOf.get(u) ?? 0) + 1);
          parent.set(v, u);
          children.get(u)?.push(v);
          children.set(v, []);
          queue.push(v);
        }
      }
    }
  }

  // Place any unvisited nodes on a final level
  const maxLevel = Array.from(levelOf.values()).reduce((m, l) => Math.max(m, l), 0);
  vertices.forEach((v) => {
    if (!levelOf.has(v.id)) {
      levelOf.set(v.id, maxLevel + 1);
      parent.set(v.id, null);
      children.set(v.id, []);
    }
  });

  // Group by level
  const levelCount = Math.max(1, ...Array.from(levelOf.values())) + 1;
  const levels: string[][] = Array.from({ length: levelCount }, () => []);
  vertices.forEach((v) => {
    const l = levelOf.get(v.id) ?? 0;
    levels[l]?.push(v.id);
  });

  // Assign x positions within each level using in-order tree traversal
  // to keep parent centred above children
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;
  const yStep = levelCount > 1 ? usableH / (levelCount - 1) : 0;

  // Compute x positions via recursive subtree width calculation
  let xCounter = 0;
  const xOf = new Map<string, number>();

  function assignX(nodeId: string): number {
    const kids = children.get(nodeId) ?? [];
    if (kids.length === 0) {
      xOf.set(nodeId, xCounter);
      xCounter += 1;
      return xOf.get(nodeId)!;
    }
    const childXs = kids.map(assignX);
    const avgX = childXs.reduce((s, x) => s + x, 0) / childXs.length;
    xOf.set(nodeId, avgX);
    return avgX;
  }

  // Find roots (nodes with null parent)
  const roots = vertices.filter((v) => parent.get(v.id) === null).map((v) => v.id);
  if (roots.length === 0) roots.push(startId);
  roots.forEach((r) => assignX(r));

  // Normalize x positions to fit viewport
  const maxX = xCounter > 0 ? xCounter - 1 : 0;
  const xScale = maxX > 0 ? usableW / maxX : 0;

  levels.forEach((layer, li) => {
    const y = padding + li * yStep;
    layer.forEach((id) => {
      const rawX = xOf.get(id) ?? 0;
      const x = padding + rawX * xScale;
      positions.set(id, { id, x, y });
    });
  });

  enforceMinSeparation(positions, vertices);
  return positions;
}

// ── Sugiyama-style layered layout ─────────────────────────────────────────────
//
// 1. BFS topological layer assignment (Coffman-Graham phase 1).
// 2. Within each layer, nodes are sorted by average Y of their predecessors
//    (barycentric heuristic) to minimise crossing count.
// 3. Layers are distributed vertically with equal spacing; within each layer
//    nodes are distributed horizontally centred on the viewport.

function computeLayeredLayout(
  vertices: Vertex[],
  edges: Edge[],
  width: number,
  height: number,
  padding: number,
): Map<string, LayoutPoint> {
  const positions = new Map<string, LayoutPoint>();

  // Build adjacency and in-degree
  const inDegree   = new Map<string, number>();
  const successors = new Map<string, string[]>();
  vertices.forEach((v) => { inDegree.set(v.id, 0); successors.set(v.id, []); });
  edges.forEach((e) => {
    successors.get(e.from)?.push(e.to);
    inDegree.set(e.to, (inDegree.get(e.to) ?? 0) + 1);
  });

  // BFS layer assignment
  const layerOf = new Map<string, number>();
  const queue: string[] = vertices
    .map((v) => v.id)
    .filter((id) => (inDegree.get(id) ?? 0) === 0);
  if (queue.length === 0) {
    // Cyclic graph: seed from the first vertex
    queue.push(vertices[0]!.id);
  }
  queue.forEach((id) => layerOf.set(id, 0));
  // O(1) membership guard — replaces O(n) queue.includes() calls
  const queued = new Set<string>(queue);

  let qi = 0;
  while (qi < queue.length) {
    const u = queue[qi++]!;
    const ul = layerOf.get(u) ?? 0;
    for (const v of successors.get(u) ?? []) {
      const current = layerOf.get(v) ?? -1;
      if (current <= ul) {
        layerOf.set(v, ul + 1);
        if (!queued.has(v)) {
          queued.add(v);
          queue.push(v);
        }
      }
    }
  }

  // Assign unvisited vertices to a final layer
  vertices.forEach((v) => {
    if (!layerOf.has(v.id)) layerOf.set(v.id, queue.length > 0 ? (layerOf.get(queue[queue.length - 1]!) ?? 0) + 1 : 0);
  });

  // Group into layers
  const layerCount = Math.max(1, ...Array.from(layerOf.values())) + 1;
  const layers: string[][] = Array.from({ length: layerCount }, () => []);
  vertices.forEach((v) => {
    const l = layerOf.get(v.id) ?? 0;
    layers[l]?.push(v.id);
  });

  // Barycentric reordering within each layer (one pass)
  for (let l = 1; l < layers.length; l++) {
    const layer = layers[l];
    if (!layer || layer.length <= 1) continue;
    const prevY = new Map<string, number>();
    layers[l - 1]?.forEach((id, i) => prevY.set(id, i));
    layer.sort((a, b) => {
      const scoreA = (successors.get(a) ?? []).map((n) => prevY.get(n) ?? 0).reduce((s, x) => s + x, 0);
      const scoreB = (successors.get(b) ?? []).map((n) => prevY.get(n) ?? 0).reduce((s, x) => s + x, 0);
      return scoreA - scoreB;
    });
  }

  // Assign pixel coordinates
  const usableH = height - padding * 2;
  const usableW = width  - padding * 2;
  const yStep   = layerCount > 1 ? usableH / (layerCount - 1) : 0;

  layers.forEach((layer, li) => {
    if (!layer || layer.length === 0) return;
    const y    = padding + li * yStep;
    const xStep = layer.length > 1 ? usableW / (layer.length - 1) : 0;
    layer.forEach((id, ni) => {
      const x = layer.length === 1
        ? width / 2
        : padding + ni * xStep;
      positions.set(id, { id, x, y });
    });
  });

  // Enforce minimum separation (post-pass)
  enforceMinSeparation(positions, vertices);

  return positions;
}

// ── Force-directed layout ─────────────────────────────────────────────────────
//
// Velocity-Verlet integration with:
//   • Coulomb repulsion: F = k² / d  (linear, not inverse-square, for smoother
//     settling on sparse graphs)
//   • Hooke spring: F = c × (d − desired)  per edge
//   • Centre gravity: nudges every node toward viewport centre each iteration
//   • Exponential cooling: displacement shrinks each iteration so the simulation
//     converges rather than oscillating
//   • Hard bounding box clamp each iteration

function computeForceLayout(
  vertices: Vertex[],
  edges: Edge[],
  width: number,
  height: number,
  padding: number,
): Map<string, LayoutPoint> {
  const positions = new Map<string, LayoutPoint>();
  const cx = width / 2;
  const cy = height / 2;

  // Seed on a circle so nodes start evenly spaced
  const seedR = Math.min(width, height) / 2 - padding;
  const coords = vertices.map((v, i) => {
    const angle = (2 * Math.PI * i) / vertices.length - Math.PI / 2;
    return { id: v.id, x: cx + seedR * Math.cos(angle), y: cy + seedR * Math.sin(angle) };
  });

  const ITERATIONS      = 300;
  const K               = Math.sqrt((width * height) / Math.max(vertices.length, 1)) * 0.9;
  const SPRING_C        = 0.06;
  const SPRING_DESIRED  = Math.max(80, K * 1.1);
  const GRAVITY         = 0.015;
  const MAX_DISPLACEMENT = K * 0.5;
  const ENERGY_THRESHOLD = 0.5;  // stop when largest displacement is below this

  const idxOf = new Map<string, number>();
  coords.forEach((c, i) => idxOf.set(c.id, i));

  for (let iter = 0; iter < ITERATIONS; iter++) {
    const temp = MAX_DISPLACEMENT * Math.exp(-4 * (iter / ITERATIONS));
    const disp: { x: number; y: number }[] = coords.map(() => ({ x: 0, y: 0 }));

    // Repulsion between every pair
    for (let i = 0; i < coords.length; i++) {
      for (let j = i + 1; j < coords.length; j++) {
        const a = coords[i]!;
        const b = coords[j]!;
        const dx   = a.x - b.x;
        const dy   = a.y - b.y;
        const dist = Math.max(Math.hypot(dx, dy), 0.01);
        const f    = (K * K) / dist;
        const ux   = dx / dist;
        const uy   = dy / dist;
        disp[i]!.x += ux * f;
        disp[i]!.y += uy * f;
        disp[j]!.x -= ux * f;
        disp[j]!.y -= uy * f;
      }
    }

    // Spring attraction along edges
    edges.forEach((edge) => {
      const fi = idxOf.get(edge.from);
      const ti = idxOf.get(edge.to);
      if (fi === undefined || ti === undefined) return;
      const a = coords[fi]!;
      const b = coords[ti]!;
      const dx      = b.x - a.x;
      const dy      = b.y - a.y;
      const dist    = Math.max(Math.hypot(dx, dy), 0.01);
      const desired = SPRING_DESIRED + (edge.weight ?? 0) * 2;
      const f       = SPRING_C * (dist - desired);
      const ux      = dx / dist;
      const uy      = dy / dist;
      disp[fi]!.x += ux * f;
      disp[fi]!.y += uy * f;
      disp[ti]!.x -= ux * f;
      disp[ti]!.y -= uy * f;
    });

    // Centre gravity + apply displacement
    let maxMove = 0;
    coords.forEach((node, i) => {
      const d = disp[i]!;
      d.x += (cx - node.x) * GRAVITY;
      d.y += (cy - node.y) * GRAVITY;

      const dLen = Math.hypot(d.x, d.y);
      if (dLen > temp) {
        d.x = (d.x / dLen) * temp;
        d.y = (d.y / dLen) * temp;
      }

      maxMove = Math.max(maxMove, dLen);

      node.x = Math.min(width - padding, Math.max(padding, node.x + d.x));
      node.y = Math.min(height - padding, Math.max(padding, node.y + d.y));
    });

    // Early stop if the layout has settled
    if (maxMove < ENERGY_THRESHOLD) break;
  }

  coords.forEach((c) => positions.set(c.id, { id: c.id, x: c.x, y: c.y }));
  enforceMinSeparation(positions, vertices);
  return positions;
}

// ── Post-pass: enforce minimum node separation ────────────────────────────────
//
// After any layout, push overlapping nodes apart until no two centres are
// closer than MIN_SEPARATION.  Maximum 20 relaxation passes.

function enforceMinSeparation(
  positions: Map<string, LayoutPoint>,
  vertices: Vertex[],
): void {
  const ids = vertices.map((v) => v.id);
  for (let pass = 0; pass < 20; pass++) {
    let moved = false;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = positions.get(ids[i]!)!;
        const b = positions.get(ids[j]!)!;
        const dx   = b.x - a.x;
        const dy   = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        if (dist < MIN_SEPARATION && dist > 0.001) {
          const push  = (MIN_SEPARATION - dist) / 2;
          const ux    = dx / dist;
          const uy    = dy / dist;
          positions.set(ids[i]!, { ...a, x: a.x - ux * push, y: a.y - uy * push });
          positions.set(ids[j]!, { ...b, x: b.x + ux * push, y: b.y + uy * push });
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
}

// ── Edge geometry helpers ─────────────────────────────────────────────────────

/**
 * Trim the line segment (from → to) so both endpoints sit on the outer circle
 * boundary of each node rather than at the node's centre.
 *
 * arrowClearance adds extra retreat on the `to` side so SVG arrowhead markers
 * don't overlap the node fill.
 */
export function trimEdgeEndpoints(
  from: LayoutPoint,
  to: LayoutPoint,
  nodeRadius: number = NODE_RADIUS,
  arrowClearance = 0,
): { x1: number; y1: number; x2: number; y2: number } {
  const dx  = to.x - from.x;
  const dy  = to.y - from.y;
  const len = Math.hypot(dx, dy);

  if (len <= (nodeRadius + arrowClearance) * 2 + 0.001) {
    return { x1: from.x, y1: from.y, x2: to.x, y2: to.y };
  }

  const ux = dx / len;
  const uy = dy / len;

  return {
    x1: from.x + ux * nodeRadius,
    y1: from.y + uy * nodeRadius,
    x2: to.x  - ux * (nodeRadius + arrowClearance),
    y2: to.y  - uy * (nodeRadius + arrowClearance),
  };
}

/**
 * Compute a perpendicular offset for parallel bidirectional edges so the two
 * opposing lines don't perfectly overlap.  Returns a (dx, dy) signed offset
 * to apply to the midpoint of each half of the parallel pair.
 *
 * Pass `isForward = true` for the A→B direction and `false` for B→A.
 */
export function computeParallelOffset(
  from: LayoutPoint,
  to: LayoutPoint,
  magnitude = 12,
  isForward = true,
): { ox: number; oy: number } {
  const dx  = to.x - from.x;
  const dy  = to.y - from.y;
  const len = Math.max(Math.hypot(dx, dy), 0.001);
  const sign = isForward ? 1 : -1;
  return { ox: (-dy / len) * magnitude * sign, oy: (dx / len) * magnitude * sign };
}
